import type { Order } from "./orders-model.js";

/**
 * When to sound the new-order alarm, and when to stop.
 *
 * Pure, so the rules can be tested without an AudioContext or a timer. The audio itself lives in
 * `alarm.ts`, which is deliberately thin.
 *
 * The console polls every 15 seconds rather than holding a socket open. That is enough to drive an
 * alarm: a beep within 15 seconds of an order arriving is fine for a kitchen. It also means the
 * alarm cannot be the thing that makes delivery reliable — if this tab is muted, closed, or the
 * tablet has rebooted, nobody hears anything. That is covered server-side by escalation (see
 * docs/ORDER-DELIVERY.md), which is what lets the client stay this simple.
 */

/** Orders that need somebody to look at them. Only new ones ring. */
export function pendingIds(orders: readonly Order[]): string[] {
  return orders.filter((o) => o.state === "SUBMITTED").map((o) => o.orderId);
}

export interface AlarmState {
  /**
   * Pending order ids already accounted for.
   *
   * `undefined` means nothing has been observed yet, which is NOT the same as "no orders pending":
   * opening the console at lunchtime with five orders already waiting must not set the alarm off.
   * The first observation seeds this silently.
   */
  known?: readonly string[];
  ringing: boolean;
  /**
   * How many distinct alert events have happened.
   *
   * Needed because the chime is now three cycles rather than a loop. Without it, a second order arriving
   * after those three had finished would be SILENT: `ringing` is already true, so nothing changes and
   * nothing re-triggers. Incrementing on every fresh batch gives each new arrival its own three chimes.
   */
  rings: number;
}

export function initialAlarmState(): AlarmState {
  return { ringing: false, rings: 0 };
}

/**
 * Fold a fresh poll into the alarm state.
 *
 * - First observation seeds silently.
 * - A pending id we have not seen before starts the alarm.
 * - While ringing, more arrivals change nothing; it is already ringing.
 * - The alarm stops on its own once nothing is pending, so ACCEPTING the order silences it. That is
 *   the behaviour we want: the alarm asks for attention, and attention is what stops it.
 *
 * `known` is pruned to what is currently pending, which bounds it. Safe because an order never
 * returns to SUBMITTED — the order state machine has no path back — so a pruned id cannot reappear
 * and ring twice.
 */
export function observeOrders(prev: AlarmState, pending: readonly string[]): AlarmState {
  if (!prev.known) return { known: [...pending], ringing: false, rings: prev.rings };
  const fresh = pending.filter((id) => !prev.known!.includes(id));
  return {
    known: [...pending],
    ringing: prev.ringing ? pending.length > 0 : fresh.length > 0,
    // Counts arrivals, not rings started, so it also increments while already ringing.
    rings: fresh.length > 0 ? prev.rings + 1 : prev.rings
  };
}

/**
 * Silence the alarm without touching the order.
 *
 * Deliberately NOT "accept the order": a button that both silences and accepts would let somebody
 * accept an order they have not read, to make a noise stop. Silencing is an admission that you
 * heard it, nothing more, and the server keeps escalating until the order is actually actioned.
 */
export function acknowledge(prev: AlarmState): AlarmState {
  return { ...prev, ringing: false };
}

/** How many orders are waiting, for the badge next to the control. */
export function waitingCount(state: AlarmState): number {
  return state.known?.length ?? 0;
}

// --- preference ---------------------------------------------------------------

const SOUND_KEY = "glowin.orders.sound";

/**
 * Sound is OFF by default, and remembered per device.
 *
 * Per device rather than per user because the same owner uses a kitchen tablet and a laptop, and
 * wants different answers on each. Off by default because an unexpected alarm on a laptop in an
 * office is the more annoying failure — a kitchen turns it on once and it sticks.
 */
export function loadSoundEnabled(
  storage: Pick<Storage, "getItem">,
  fallback = false
): boolean {
  try {
    const stored = storage.getItem(SOUND_KEY);
    // Distinguish "never chosen" from "chosen off". A saved choice always wins over the device default,
    // so someone who deliberately silenced a tablet is not overridden on their next visit.
    if (stored === null) return fallback;
    return stored === "on";
  } catch {
    // Private browsing and blocked storage both throw. Fall back rather than crashing a till.
    return fallback;
  }
}

/**
 * Should sound start ON for this device?
 *
 * Yes on a touch device: that is a kitchen tablet, where a missed order is a lost customer and nobody
 * will think to switch sound on. No on a fine pointer: that is a laptop, where an unexpected alarm in
 * an office is the more annoying failure.
 *
 * This only sets the PREFERENCE. Browsers still require a user gesture before audio may play, so
 * defaulting on cannot by itself make a sound — it makes the console show "Tap to enable sound" instead
 * of a grey "Sound off" that looks deliberate, and the page arms itself on the first tap anywhere.
 */
export function defaultSoundEnabled(coarsePointer: boolean): boolean {
  return coarsePointer;
}

export function saveSoundEnabled(storage: Pick<Storage, "setItem">, enabled: boolean): void {
  try {
    storage.setItem(SOUND_KEY, enabled ? "on" : "off");
  } catch {
    // Preference simply doesn't persist; the session still works.
  }
}

/**
 * What the control should say.
 *
 * `enabled` is the saved preference; `armed` is whether audio can actually play in this tab, which
 * needs a user gesture the browser has accepted. The two differ after a reload — the preference
 * survives, the gesture does not — and that gap is exactly how a kitchen ends up believing the alarm
 * is on while the tab makes no sound. It gets its own visible state rather than being hidden.
 */
export type SoundStatus = "off" | "on" | "needs-gesture";

export function soundStatus(enabled: boolean, armed: boolean): SoundStatus {
  if (!enabled) return "off";
  return armed ? "on" : "needs-gesture";
}

export function soundStatusLabel(status: SoundStatus): string {
  switch (status) {
    case "off":
      return "Sound off";
    case "on":
      return "Sound on";
    case "needs-gesture":
      return "Tap to enable sound";
  }
}
